// Brown noise for the Basement overlay (2026-10-30, docs/plans/basement-overlay.md): it fades in with the overlay as
// this visitor nears the room's centre. The noise is the Voyage bridge's generator (brown-noise.ts), its own looping
// buffer, through one GainNode straight to the master (not placed in the room: no panner, no reverb).
//
// Every change glides: the gain is held where it is and set on its way to the new target with setTargetAtTime (time
// constant glide_s); no direct value sets. At most one update per 33 ms (one pending automation event per param:
// WebKit's audio thread stalled on piles of events, engine.ts). The source starts with the first target above 0 and
// stops STOP_AFTER_S after the target went to 0 (by then the gain is ~70 dB down), so it costs nothing far away.

import { makeBrownNoise } from './brown-noise';
import { hold } from './speakers';

/** Updates closer together than this (ms) wait for the next call (a target of 0 goes at once). */
const MIN_INTERVAL_MS = 33;
/** The source stops this many glide time constants after the target went to 0 (exp(-8) = -69 dB). */
const STOP_AFTER_TAU = 8;

export class OverlayNoise {
  readonly gain: GainNode;
  private src: AudioBufferSourceNode | null = null;
  private buffer: AudioBuffer | null = null;
  /** The target last set on the param. */
  target = 0;
  private last = -Infinity;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  /** Updates applied, sources started (tests). */
  readonly stats = { updates: 0, starts: 0 };

  constructor(private ctx: BaseAudioContext, out: AudioNode, private now: () => number = () => performance.now()) {
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    this.gain.connect(out);
  }

  /** Glide toward `level` with the time constant `tau` (s). Call as often as wanted (rate limited here). */
  set(level: number, tau: number): void {
    level = Math.max(0, level);
    if (Math.abs(level - this.target) < 1e-5) return;
    const t = this.now();
    if (level > 0 && t - this.last < MIN_INTERVAL_MS) return; // the next frame's call brings it
    this.last = t;
    this.target = level;
    this.stats.updates++;
    const at = this.ctx.currentTime;
    if (level > 0) {
      if (this.stopTimer) { clearTimeout(this.stopTimer); this.stopTimer = null; }
      if (!this.src) this.start(at);
    }
    const p = this.gain.gain;
    hold(p, at);
    p.setTargetAtTime(level, at, tau);
    if (level === 0 && this.src && !this.stopTimer) {
      this.stopTimer = setTimeout(() => { this.stopTimer = null; if (this.target === 0) this.stop(); }, tau * STOP_AFTER_TAU * 1000);
    }
  }

  private start(at: number): void {
    this.buffer ??= makeBrownNoise(this.ctx);
    const s = (this.src = this.ctx.createBufferSource());
    s.buffer = this.buffer;
    s.loop = true;
    s.connect(this.gain);
    s.start(at);
    this.stats.starts++;
  }

  private stop(): void {
    const s = this.src;
    if (!s) return;
    this.src = null;
    s.stop();
    s.disconnect();
  }

  get running(): boolean {
    return !!this.src;
  }
}
